import React, { useState, useEffect } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { data } = useSWR("/settings", fetcher);
  const [form, setForm] = useState({ company_name: "", owner_name: "", company_phone: "", company_email: "", lang: "en" });

  useEffect(() => {
    if (data) setForm({
      company_name: data.company_name || "",
      owner_name: data.owner_name || "",
      company_phone: data.company_phone || "",
      company_email: data.company_email || "",
      lang: data.lang || "en",
    });
  }, [data]);

  const save = async () => {
    try {
      await api.patch("/settings", form);
      setLang(form.lang);
      toast.success("Saved");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="// 11 / Settings" title={t("settings.title")} sub={t("settings.sub")} />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
          <div className="border border-zinc-200">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">Company / Entreprise</p></div>
            <div className="p-5 space-y-3">
              <Field label={t("settings.companyName")}><Input data-testid="set-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("settings.ownerName")}><Input data-testid="set-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("common.phone")}><Input data-testid="set-phone" value={form.company_phone} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} className="rounded-none mono" /></Field>
              <Field label={t("common.email")}><Input data-testid="set-email" value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} className="rounded-none mono" /></Field>
            </div>
          </div>
          <div className="border border-zinc-200 h-fit">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">Preferences / Préférences</p></div>
            <div className="p-5 space-y-3">
              <Field label={t("settings.language")}>
                <Select value={form.lang} onValueChange={(v) => setForm({ ...form, lang: v })}>
                  <SelectTrigger data-testid="set-lang" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        </div>
        <div className="mt-6 max-w-4xl">
          <Button data-testid="set-save" onClick={save} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider h-11 px-6">
            <FloppyDisk size={14} className="mr-2" /> {t("common.save")}
          </Button>
        </div>
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
